import { useEffect } from 'react';
import { SITE_URL } from '@/config/site';

type JsonLd = Record<string, unknown>;

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: JsonLd | JsonLd[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageSeo(options: SeoOptions) {
  const { title, description, keywords, path, ogType = 'website', ogImage, jsonLd } = options;

  useEffect(() => {
    const canonical = `${SITE_URL}${path === '/' ? '' : path}`;
    const image = ogImage || 'https://readdy.ai/api/search-image?query=warm%20abstract%20gradient%20poster%20with%20orange%20amber%20glow%20on%20cream%20background%2C%20minimal%20editorial%20style%2C%20no%20text&width=1200&height=630&seq=chigua-og-01&orientation=landscape';

    document.title = title;
    document.documentElement.lang = 'zh-CN';

    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
    upsertMeta('name', 'author', '吃瓜观察局');
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:site_name', '吃瓜观察局');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:locale', 'zh_CN');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    const scriptId = 'page-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.textContent = '';
    }
  }, [title, description, keywords, path, ogType, ogImage, jsonLd]);
}